import { configureStore, createSlice } from '@reduxjs/toolkit';
import { resolveRole } from '../../shared/utils/jwt';

const USER_PORTAL_STORAGE_KEY = 'zopeo_user_portal';

const isStorageAvailable = () => typeof window !== 'undefined' && window.localStorage;

const readStorage = (key) => {
  if (!isStorageAvailable()) {
    return null;
  }
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  if (!isStorageAvailable()) {
    return;
  }
  try {
    if (value === null || value === undefined || value === '') {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    return;
  }
};

const storedUserPortalData = readStorage(USER_PORTAL_STORAGE_KEY) || {};

const getInitialRoute = () => {
  if (typeof window === 'undefined') {
    return '/';
  }
  const path = window.location.pathname || '/';
  const search = window.location.search || '';
  const fullPath = `${path || '/'}${search}`;
  if (fullPath && !fullPath.startsWith('/')) {
    return `/${fullPath}`;
  }
  return fullPath || '/';
};

const getDeviceUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'device-uuid';
};

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token: '',
    role: 'user',
    sessionChecked: false
  },
  reducers: {
    setAuthFromToken: (state, action) => {
      const { token, roleHint } = action.payload || {};
      state.token = token || '';
      state.role = resolveRole(token, roleHint);
      state.sessionChecked = true;
    },
    clearAuthState: (state) => {
      state.token = '';
      state.role = 'user';
      state.sessionChecked = true;
    },
    setSessionChecked: (state, action) => {
      state.sessionChecked = Boolean(action.payload);
    }
  }
});

const { setAuthFromToken, clearAuthState, setSessionChecked } = authSlice.actions;

const routeSlice = createSlice({
  name: 'route',
  initialState: { path: getInitialRoute() },
  reducers: {
    setRoute: (state, action) => {
      state.path = action.payload || '/';
    }
  }
});

const activitySlice = createSlice({
  name: 'activity',
  initialState: { items: [] },
  reducers: {
    pushActivity: (state, action) => {
      state.items = [
        action.payload,
        ...state.items
      ].slice(0, 6);
    }
  }
});

const storeSlice = createSlice({
  name: 'store',
  initialState: {
    storeToken: '',
    storeUuid: '',
    storeMallUuid: '',
    storeName: '',
    storeRegister: {
      storeName: '',
      email: '',
      phone: '',
      password: '',
      storeCode: '',
      mallUuid: '',
      brandUuid: '',
      floorNumber: '',
      zoneWing: '',
      city: '',
      state: '',
      pincode: '',
      parkingZones: '',
      totalBillingSections: '',
      totalBillingCounters: '',
      totalTrialSections: '',
      totalTrialRooms: '',
      hasParkingIntegration: 'false',
      chargesTaxSeparately: false,
      isNewMall: false
    },
    storeLogin: {
      email: '',
      storeCode: '',
      password: ''
    },
    cashier: {
      storeUuid: '',
      sectionUuid: ''
    }
  },
  reducers: {
    setStoreRegisterField: (state, action) => {
      const { field, value } = action.payload;
      state.storeRegister[field] = value;
    },
    setStoreLoginField: (state, action) => {
      const { field, value } = action.payload;
      state.storeLogin[field] = value;
    },
    setCashierField: (state, action) => {
      const { field, value } = action.payload;
      state.cashier[field] = value;
    },
    setStoreToken: (state, action) => {
      state.storeToken = action.payload || '';
    },
    setStoreUuid: (state, action) => {
      state.storeUuid = action.payload || '';
    },
    setStoreMallUuid: (state, action) => {
      state.storeMallUuid = action.payload || '';
    },
    setStoreName: (state, action) => {
      state.storeName = action.payload || '';
    }
  },
  extraReducers: (builder) => {
    builder.addCase(setAuthFromToken, (state, action) => {
      const { token, roleHint } = action.payload || {};
      const role = resolveRole(token, roleHint);
      state.storeToken = role === 'store' ? (token || '') : '';
    });
  }
});

const userSlice = createSlice({
  name: 'user',
  initialState: {
    scanContext: {
      storeUuid: storedUserPortalData.scanContext?.storeUuid || '',
      mallUuid: storedUserPortalData.scanContext?.mallUuid || '',
      sectionUuid: storedUserPortalData.scanContext?.sectionUuid || '',
      floorNumber: storedUserPortalData.scanContext?.floorNumber || '',
      deviceUuid: storedUserPortalData.scanContext?.deviceUuid || getDeviceUuid()
    },
    sessionDetails: {
      sessionId: storedUserPortalData.sessionDetails?.sessionId || '',
      phone: storedUserPortalData.sessionDetails?.phone || '',
      sectionUuid: storedUserPortalData.sessionDetails?.sectionUuid || '',
      queueUuid: storedUserPortalData.sessionDetails?.queueUuid || ''
    }
  },
  reducers: {
    setScanContextField: (state, action) => {
      const { field, value } = action.payload;
      state.scanContext[field] = value;
    },
    setSessionDetailsField: (state, action) => {
      const { field, value } = action.payload;
      state.sessionDetails[field] = value;
    }
  }
});

export { setAuthFromToken };
export { clearAuthState, setSessionChecked };
export const { setRoute } = routeSlice.actions;
export const { pushActivity } = activitySlice.actions;
export const {
  setStoreRegisterField,
  setStoreLoginField,
  setCashierField,
  setStoreToken,
  setStoreUuid,
  setStoreMallUuid,
  setStoreName
} = storeSlice.actions;
export const { setScanContextField, setSessionDetailsField } = userSlice.actions;

const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    route: routeSlice.reducer,
    activity: activitySlice.reducer,
    store: storeSlice.reducer,
    user: userSlice.reducer
  }
});

store.subscribe(() => {
  const state = store.getState();
  writeStorage(USER_PORTAL_STORAGE_KEY, state.user);
});

export default store;
